import React, { ReactElement } from 'react'
import styled from 'styled-components'
import { toSentenceCase } from '../helpers/dataTransformer'
import colors from '../lib/colors'
import theme from '../lib/theme'
import { GenericObject } from '../types/typeAliases'

const Styles = styled.div`
  cursor: pointer;
  user-select: none;
  display: inline-flex;
  align-items: center;
  width: fit-content;
  margin-right: 20px;
  &:last-child {
    margin-right: 0;
  }
  &:hover .radio-unchecked {
    border-color: ${colors.darkerGray};
  }
  &.radio-disabled {
    opacity: 0.5;
    pointer-events: none;
  }
  &.radio-error {
    color: ${colors.red};
    .radio-circle {
      background-color: ${colors.lightestRed};
      border-color: ${colors.red};
    }
    .radio-checked {
      background-color: ${colors.red};
      border-color: ${colors.red};
    }
  }
`
const Circle = styled.div`
  border: 1px solid ${colors.darkGray};
  min-width: 15px;
  min-height: 15px;
  border-radius: 50%;
  margin-right: 5px;
  transition: 0.3s;
  position: relative;
  &.radio-checked {
    transition: 0s;
    background: ${colors.black};
    border-color: ${colors.black};
    :after {
      content: '';
      position: absolute;
      ${theme.inset0};
      margin: auto;
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: ${colors.white};
    }
  }
`
const Label = styled.div`
  font-size: 0.9rem;
  font-weight: bold;
  margin-left: 2px;
  margin-bottom: 7px;
`
export interface IRadioOption<T> {
  value: T
  label: string
}

export const enumToRadioOptions = <T extends {}>(
  enumObject: GenericObject<T>,
): IRadioOption<T>[] => {
  const options = Object.keys(enumObject).map((label) => ({
    value: enumObject[label],
    label: toSentenceCase(label),
  }))
  return options
}

export const stringsToRadioOptions = (
  input: string[],
): IRadioOption<string>[] => input.map((value) => ({
  value,
  label: value,
}))

export interface IRadioProps<T> {
  options: IRadioOption<T>[]
  onChange: (value: T) => void
  value?: T
  disabled?: boolean
  isError?: boolean
  label?: string
}

export default function Radio<T extends unknown>({
  options,
  onChange,
  value,
  disabled,
  isError,
  label,
}: IRadioProps<T>): ReactElement {
  const wrapperDisabled = disabled ? 'radio-disabled' : ''
  const wrapperError = isError ? 'radio-error' : ''

  const render = options.map((option) => {
    const radioChecked = value === option.value ? 'radio-checked' : 'radio-unchecked'

    return (
      <Styles
        key={option.label}
        onClick={(): void => {
          onChange(option.value)
        }}
        className={`${wrapperDisabled} ${wrapperError}`}
      >
        <Circle className={`radio-circle ${radioChecked}`} />
        {option.label}
      </Styles>
    )
  })

  return (
    <div>
      {label && <Label>{label}</Label>}
      {render}
    </div>
  )
}
